import { fetchAvailableFilters, FilterOption } from './reportsDataService';
import { newReportConfig } from '../features/newMdmReports/mdmReportsUtils';

const GLOBAL_FILTERS = ["distributor_code"];

/**
 * Load and process custom filters for a report
 * Filters out hidden filters, global filters, and merged filters (including their source aliases) based on report configuration
 * 
 * @param reportConfig - The report configuration object
 * @returns Promise resolving to array of processed FilterOption objects
 */
export async function loadCustomFiltersForReport(
  reportConfig: newReportConfig | null
): Promise<FilterOption[]> {
  if (!reportConfig?.reportName) {
    return [];
  }

  try {
    const availableFilters = await fetchAvailableFilters(reportConfig.reportName);
    
    const filtersToHide = reportConfig.filtersToHide || [];
    

    const filteredAvailableFilters = availableFilters.filter(
      filter => !filtersToHide.includes(filter.alias)
    );
    const filteredGlobalFilters = filteredAvailableFilters.filter(
      filter => !GLOBAL_FILTERS.includes(filter.alias)
    );
    const mergedFilterAliases = new Set<string>();
    const mergedFilterSourceAliases = new Set<string>();
    
    if (reportConfig?.mergedFilters) {
      Object.keys(reportConfig.mergedFilters).forEach(mergedFilterAlias => {
        mergedFilterAliases.add(mergedFilterAlias);
        const sources = reportConfig.mergedFilters?.[mergedFilterAlias] || [];
        sources.forEach(source => {
          mergedFilterSourceAliases.add(source.alias);
        });
      });
    }
    const finalFilteredAvailableFilters = filteredGlobalFilters.filter(
      filter => !mergedFilterAliases.has(filter.alias) && !mergedFilterSourceAliases.has(filter.alias)
    );
    
    return finalFilteredAvailableFilters;
  } catch (error) {
    console.error('Error loading custom filters:', error);
    return [];
  }
}

/**
 * Check if a filter is a merged filter based on report configuration
 * 
 * @param filterAlias - The filter alias to check
 * @param reportConfig - The report configuration object
 * @returns true if the filter is a merged filter, false otherwise
 */
export function isMergedFilterForReport(
  filterAlias: string,
  reportConfig: newReportConfig | null
): boolean {
  return Boolean(reportConfig?.mergedFilters?.[filterAlias]);
}

/**
 * Get merged filter sources for a given filter alias
 * 
 * @param filterAlias - The merged filter alias
 * @param reportConfig - The report configuration object
 * @returns Array of merged filter sources or undefined
 */
export function getMergedFilterSources(
  filterAlias: string,
  reportConfig: newReportConfig | null
) {
  return reportConfig?.mergedFilters?.[filterAlias];
}

